/**
 * Quem está usando, e o que essa pessoa pode fazer.
 *
 * Dois papéis: leitor vê tudo, operador também dispara varredura. A distinção
 * não é hierarquia — é que varredura consome as requisições de uma conta única
 * do Licit Mais, e conta bloqueada não volta com pedido de desculpas.
 */
import { createContext, useContext, type ReactNode } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api, ErroApi, type Usuario } from "./api/cliente";

interface Sessao {
  usuario: Usuario | null;
  carregando: boolean;
  podeOperar: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => Promise<void>;
  erroEntrada: string | null;
}

const Contexto = createContext<Sessao | null>(null);

export function ProvedorSessao({ children }: { children: ReactNode }) {
  const cliente = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["eu"],
    queryFn: api.eu,
    retry: (falhas, erro) =>
      // 401 é resposta, não falha de rede: insistir só atrasa a tela de login.
      erro instanceof ErroApi && erro.status === 401 ? false : falhas < 2,
  });

  const entrada = useMutation({
    mutationFn: ({ email, senha }: { email: string; senha: string }) =>
      api.entrar(email, senha),
    onSuccess: (u) => cliente.setQueryData(["eu"], u),
  });

  const saida = useMutation({
    mutationFn: api.sair,
    onSuccess: () => cliente.clear(),
  });

  const valor: Sessao = {
    usuario: data ?? null,
    carregando: isLoading,
    podeOperar: data?.papel === "operador",
    entrar: async (email, senha) => {
      await entrada.mutateAsync({ email, senha });
    },
    sair: async () => {
      await saida.mutateAsync();
    },
    erroEntrada: entrada.error instanceof Error ? entrada.error.message : null,
  };

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSessao(): Sessao {
  const s = useContext(Contexto);
  if (!s) throw new Error("useSessao fora do ProvedorSessao");
  return s;
}
